import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listProducts, listReleases, type ReleaseChange, type ReleaseList } from '../../api/releases';

export const productsKey = ['products'] as const;
export const releasesKey = (productId: string) => ['releases', productId] as const;

/** Products with their release summary (GET /api/products). */
export function useProducts() {
  return useQuery({ queryKey: productsKey, queryFn: listProducts });
}

export function useReleaseList(productId: string) {
  return useQuery({ queryKey: releasesKey(productId), queryFn: () => listReleases(productId) });
}

/**
 * Runs a release-changing call, then writes the returned list into the cache and refreshes the
 * product cards (their current/draft/outdated numbers change with every release action).
 */
export function useReleaseMutation<V>(productId: string, fn: (v: V) => Promise<ReleaseChange>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      qc.setQueryData<ReleaseList | undefined>(releasesKey(productId), (old) =>
        old ? { ...old, current: r.current, releases: r.releases } : old);
      void qc.invalidateQueries({ queryKey: releasesKey(productId) });
      void qc.invalidateQueries({ queryKey: productsKey });
    },
  });
}
